import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Bug,
  AlertTriangle,
  Shield,
  TrendingUp,
  ExternalLink,
  Server,
  Code,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// Types
type CveSeverity = "critical" | "high" | "medium" | "low" | "none";

interface VulnCve {
  cve_id: string;
  cvss_score?: number;
  severity: CveSeverity;
  epss_score?: number;
  is_kev?: boolean;
}

interface VulnerableService {
  source: "portscan" | "tech";
  // For portscan source
  host?: string;
  port?: number;
  protocol?: string;
  service?: string;
  product?: string;
  version?: string;
  // For tech source
  technology?: string;
  // Common fields
  cve_count: number;
  highest_severity?: CveSeverity;
  cves: VulnCve[];
}

interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface VulnModuleData {
  score?: number;
  sources?: string[];
  total_cves?: number;
  by_severity?: SeverityCounts;
  vulnerable_services?: VulnerableService[];
  status?: string;
  message?: string;
}

export interface VulnModuleViewProps {
  data: VulnModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  hideHeader?: boolean;
}

// Severity colors
const SEVERITY_CONFIG: Record<CveSeverity, { label: string; color: string; bgColor: string }> = {
  critical: { label: "Critical", color: "text-red-400", bgColor: "bg-red-500/15 border-red-500/40" },
  high: { label: "High", color: "text-orange-400", bgColor: "bg-orange-500/15 border-orange-500/40" },
  medium: { label: "Medium", color: "text-amber-400", bgColor: "bg-amber-500/15 border-amber-500/40" },
  low: { label: "Low", color: "text-blue-400", bgColor: "bg-blue-500/15 border-blue-500/40" },
  none: { label: "Info", color: "text-slate-400", bgColor: "bg-slate-500/15 border-slate-500/40" },
};

// Format EPSS score
function formatEpss(score?: number): string {
  if (score === undefined) return "—";
  return `${(score * 100).toFixed(1)}%`;
}

// Expandable CVE List
function CveList({ cves }: { cves: VulnCve[] }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const displayCount = isExpanded ? cves.length : Math.min(3, cves.length);
  const hasMore = cves.length > 3;

  return (
    <div className="space-y-1.5">
      {cves.slice(0, displayCount).map((cve) => {
        const severityConfig = SEVERITY_CONFIG[cve.severity];
        return (
          <div
            key={cve.cve_id}
            className={cn(
              "p-2 rounded border text-xs flex items-center justify-between gap-2 flex-wrap",
              severityConfig.bgColor
            )}
          >
            <div className="flex items-center gap-2">
              <a
                href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={cn("font-mono font-semibold hover:underline flex items-center gap-1", severityConfig.color)}
              >
                {cve.cve_id}
                <ExternalLink className="h-3 w-3" />
              </a>
              <Badge variant="outline" className={cn("text-xs", severityConfig.bgColor, severityConfig.color)}>
                {severityConfig.label}
              </Badge>
              {cve.cvss_score !== undefined && (
                <span className="text-muted-foreground">
                  CVSS: <span className={severityConfig.color}>{cve.cvss_score.toFixed(1)}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              {cve.epss_score !== undefined && (
                <span className="flex items-center gap-1" title="EPSS: Probability of exploitation">
                  <TrendingUp className="h-3 w-3" />
                  {formatEpss(cve.epss_score)}
                </span>
              )}
              {cve.is_kev && (
                <Badge variant="destructive" className="text-xs h-5">KEV</Badge>
              )}
            </div>
          </div>
        );
      })}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-6 text-xs w-full"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show {cves.length - 3} more CVE{cves.length - 3 !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// Vulnerable Service Card
function VulnerableServiceCard({ service }: { service: VulnerableService }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const severityConfig = service.highest_severity ? SEVERITY_CONFIG[service.highest_severity] : SEVERITY_CONFIG.none;

  return (
    <div className={cn("p-4 rounded-lg border", severityConfig.bgColor)}>
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          {service.source === "portscan" ? (
            <Server className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Code className="h-4 w-4 text-muted-foreground" />
          )}
          <div>
            {service.source === "portscan" ? (
              <>
                <span className="font-mono text-sm">{service.host}:{service.port}</span>
                {service.service && (
                  <span className="text-muted-foreground text-sm ml-2">
                    {service.service}
                    {service.product && ` (${service.product}`}
                    {service.version && ` ${service.version}`}
                    {service.product && ")"}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-medium">{service.technology}</span>
                {service.version && (
                  <span className="text-muted-foreground text-sm ml-2">v{service.version}</span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn("text-xs", severityConfig.bgColor, severityConfig.color)}>
            {service.cve_count} CVE{service.cve_count !== 1 ? "s" : ""}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-7 px-2"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      {isExpanded && <CveList cves={service.cves} />}
    </div>
  );
}

export function VulnModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: VulnModuleViewProps) {
  const totalCves = data.total_cves || 0;
  const severity = data.by_severity || { critical: 0, high: 0, medium: 0, low: 0 };
  const services = data.vulnerable_services || [];
  const sources = data.sources || [];

  // Handle disabled/unavailable status
  if (data.status === "disabled" || data.status === "unavailable") {
    return (
      <ModuleContainer
        moduleName="vuln"
        icon={Bug}
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        className={className}
        hideHeader={hideHeader}
      >
        <div className="p-8 text-center">
          <Bug className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{data.message || "Vulnerability scanning is not available"}</p>
        </div>
      </ModuleContainer>
    );
  }

  // No vulnerabilities found
  if (totalCves === 0) {
    return (
      <ModuleContainer
        moduleName="vuln"
        icon={Bug}
        data={data}
        isLoading={isLoading}
        error={error}
        score={data.score}
        onRetry={onRetry}
        className={className}
        hideHeader={hideHeader}
      >
        <div className="p-8 text-center">
          <Shield className="h-12 w-12 mx-auto text-emerald-400 mb-4" />
          <h3 className="text-lg font-semibold text-emerald-400 mb-2">No Known Vulnerabilities</h3>
          <p className="text-muted-foreground">
            No CVEs were found for the detected services and technologies.
          </p>
          {sources.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Note: Enable portscan and/or tech modules to detect services for CVE correlation.
            </p>
          )}
        </div>
      </ModuleContainer>
    );
  }

  return (
    <ModuleContainer
      moduleName="vuln"
      icon={Bug}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Bug className="h-3 w-3" /> Total CVEs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{totalCves}</p>
            </CardContent>
          </Card>
          {severity.critical > 0 && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-red-400 font-normal">Critical</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-red-400">{severity.critical}</p>
              </CardContent>
            </Card>
          )}
          {severity.high > 0 && (
            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-orange-400 font-normal">High</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-orange-400">{severity.high}</p>
              </CardContent>
            </Card>
          )}
          {severity.medium > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-amber-400 font-normal">Medium</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-amber-400">{severity.medium}</p>
              </CardContent>
            </Card>
          )}
          {severity.low > 0 && (
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-blue-400 font-normal">Low</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-blue-400">{severity.low}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Critical Alert */}
        {(severity.critical > 0 || severity.high > 0) && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div>
                <h4 className="font-medium text-red-400">Critical/High Severity Vulnerabilities</h4>
                <p className="text-sm text-muted-foreground">
                  {severity.critical + severity.high} CVE{(severity.critical + severity.high) !== 1 ? "s" : ""} require immediate attention.
                  Review the affected services below and prioritize patching.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Data Sources */}
        {sources.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>CVE data correlated from:</span>
            {sources.map((source) => (
              <Badge key={source} variant="outline" className="text-xs">
                {source === "portscan" ? "Port Scan" : source === "tech" ? "Technology Detection" : source}
              </Badge>
            ))}
          </div>
        )}

        {/* Vulnerable Services */}
        {services.length > 0 && (
          <ModuleSection
            title="Affected Services"
            icon={Server}
            badge={<Badge variant="outline" className="text-xs">{services.length}</Badge>}
          >
            <div className="space-y-3">
              {services.map((service, index) => (
                <VulnerableServiceCard key={index} service={service} />
              ))}
            </div>
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

VulnModuleView.displayName = "VulnModuleView";
